import React, { createContext, useContext, useState, useEffect } from 'react';
import { waitlistService } from '../services/waitlistService';

// Context for managing global progress state
const ProgressContext = createContext();

export const useProgress = () => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};

export const ProgressProvider = ({ children }) => {
  const [joinedMembers, setJoinedMembers] = useState(174); // Fallback values
  const [spotsLeft, setSpotsLeft] = useState(826);
  const totalSpots = 1000;
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Load real data from Supabase
  const loadStats = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await waitlistService?.getStats();
      
      if (data && !error) {
        setJoinedMembers(data?.joined_members || 0);
        setSpotsLeft(data?.spots_left || 1000);
        setError(null);
      } else {
        // Keep existing fallback values on error
        setError(error?.message || 'Failed to load stats');
      }
    } catch (err) {
      setError('Network error loading stats');
    } finally {
      setIsLoading(false);
    }
  };

  // Set up real-time subscription for live updates
  useEffect(() => {
    // Initial data load
    loadStats();

    // Subscribe to real-time updates
    const subscription = waitlistService?.subscribeToStats((updatedStats) => {
      if (updatedStats) {
        setJoinedMembers(updatedStats?.joined_members || 0);
        setSpotsLeft(updatedStats?.spots_left || 1000);
      }
    });

    // Cleanup subscription on unmount
    return () => {
      if (subscription) {
        waitlistService?.unsubscribe(subscription);
      }
    };
  }, []);

  // Manual refresh function
  const refreshStats = async () => {
    await loadStats();
  };

  // Calculate progress percentage
  const progressPercentage = (joinedMembers / totalSpots) * 100;

  const value = {
    joinedMembers,
    spotsLeft,
    totalSpots,
    progressPercentage,
    isLoading,
    error,
    refreshStats,
    // Legacy setters for compatibility (but they won't persist to database)
    setJoinedMembers,
    setSpotsLeft
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export default ProgressProvider;